/**
 * Which sections of the inspector are folded (Details, Data, Links, Choices, Files,
 * Comments), per environment: folding Links while writing a story leaves them open in Coding.
 * Kept in the browser, like the other personal choices of the page.
 */
import { createSignal } from "solid-js";
import { load, save } from "../shared/storage";

/** Where the folded sections are kept in the browser. */
const FOLDED_KEY = "developer-app-inspector-folded";

/** The folded section ids by environment id (`""`: no environment). */
type Folded = Record<string, string[]>;

/** The inspector's sections service. */
export interface InspectorSections {
  /** Whether section `id` is folded in the environment worked in. */
  folded(id: string): boolean;
  /** Folds section `id`, or opens it again. */
  toggle(id: string): void;
}

/** `folded` with `id` folded (or opened again) in `environment`. */
export function toggled(folded: Folded, environment: string, id: string): Folded {
  const now = folded[environment] ?? [];
  return { ...folded, [environment]: now.includes(id) ? now.filter((s) => s !== id) : [...now, id] };
}

/** Creates the service; `environment` gives the id of the environment worked in. */
export function createInspectorSections(environment: () => string | null): InspectorSections {
  const saved = load<unknown>(FOLDED_KEY, {});
  const [folded, setFolded] = createSignal<Folded>(saved && typeof saved === "object" && !Array.isArray(saved) ? (saved as Folded) : {});
  return {
    folded: (id) => (folded()[environment() ?? ""] ?? []).includes(id),
    toggle(id) {
      const next = toggled(folded(), environment() ?? "", id);
      setFolded(next);
      save(FOLDED_KEY, next);
    },
  };
}
